import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { StatementFilters } from '../../../../../pages/admin-dashboard/account-statement/account-statement-veiw-list/account-statement-veiw-list.model';

@Component({
  selector: 'app-account-statement-filter',
  standalone: true,
  imports: [CommonModule, FilterInput, FilterDateComponent, FilterSortingComponent],
  templateUrl: './account-statement-filter.html',
  styleUrl: './account-statement-filter.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountStatementFilterComponent {
  private locale = inject(LOCALE_ID);

  isArabic = computed(() => this.locale.startsWith('ar'));

  initialFilters = input<StatementFilters>({
    amountFrom: null,
    amountTo: null,
    dateFrom: null,
    dateTo: null,
    invoiceId: '',
    orderId: '',
    orderBy: 'INVOICE_DATE',
    orderDir: 'DESC',
  });

  applyFilters = output<StatementFilters>();
  resetFilters = output<void>();

  amountFrom = linkedSignal(() => this.initialFilters().amountFrom);
  amountTo = linkedSignal(() => this.initialFilters().amountTo);
  dateFrom = linkedSignal(() => this.initialFilters().dateFrom);
  dateTo = linkedSignal(() => this.initialFilters().dateTo);
  invoiceId = linkedSignal(() => this.initialFilters().invoiceId);
  orderId = linkedSignal(() => this.initialFilters().orderId);
  orderBy = linkedSignal(() => this.initialFilters().orderBy);
  orderDir = linkedSignal<'ASC' | 'DESC'>(() => this.initialFilters().orderDir);

  sortOptions = signal<{ value: string; labelEn: string; labelAr: string }[]>([
    { value: 'INVOICE_DATE', labelEn: 'Invoice Date', labelAr: 'تاريخ الفاتورة' },
    { value: 'PHARMACY_PRICE', labelEn: 'Pharmacy Price', labelAr: 'إجمالي السعر' },
  ]);

  filters = computed<StatementFilters>(() => ({
    amountFrom: this.amountFrom(),
    amountTo: this.amountTo(),
    dateFrom: this.dateFrom(),
    dateTo: this.dateTo(),
    invoiceId: this.invoiceId(),
    orderId: this.orderId(),
    orderBy: this.orderBy(),
    orderDir: this.orderDir(),
  }));

  amountInvalid = computed(() => {
    const from = this.amountFrom();
    const to = this.amountTo();
    return from !== null && to !== null && Number(to) < Number(from);
  });

  dateInvalid = computed(() => {
    const from = this.dateFrom();
    const to = this.dateTo();
    if (!from || !to) return false;
    return new Date(from) > new Date(to);
  });

  futureDateInvalid = computed(() => {
    const from = this.dateFrom();
    const to = this.dateTo();
    const today = new Date().toISOString().split('T')[0];
    return (!!from && from > today) || (!!to && to > today);
  });

  isFormValid = computed(() => !this.amountInvalid() && !this.dateInvalid() && !this.futureDateInvalid());

  toggleSortOrder(): void {
    this.orderDir.update((dir) => (dir === 'ASC' ? 'DESC' : 'ASC'));
  }

  onApply(): void {
    if (!this.isFormValid()) return;
    this.applyFilters.emit(this.filters());
  }

  onReset(): void {
    this.amountFrom.set(null);
    this.amountTo.set(null);
    this.dateFrom.set(null);
    this.dateTo.set(null);
    this.invoiceId.set('');
    this.orderId.set('');
    this.orderBy.set('INVOICE_DATE');
    this.orderDir.set('DESC');

    this.resetFilters.emit();
  }
}
